import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCED_OMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faBoxCheck, faUserTag } from '@fortawesome/pro-solid-svg-icons';

import { AreaDetails } from './AreaDetails';
import { AreaLocation } from './AreaLocation';

/**
 * @author @omerrkosar
 */

const AreaSettingForm = NiceModal.create(({ id, mode, modalName }) => {
	const { t } = useTranslation();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const { showError, showSuccess } = useAlert();
	const [tab, setTab] = useState(0);
	const addMode = mode !== 'edit' && mode !== 'view';
	const [isLoading, setIsLoading] = useState(false);
	const [errorTabs, setErrorTabs] = useState([]);

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const viewMode = mode === 'view';

	const nextStep = () => {
		if (tab < 1) {
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const schema = useMemo(
		() =>
			!addMode
				? yup
						.object()
						.shape({
							areaName: yup.string().nullable().required(),
							countryName: yup.string().nullable(),
							cityName: yup.string().nullable(),
							capacity: yup.number().nullable().required(),
							description: yup.string().nullable(),
							priority: yup.string().nullable().required(),
							drivers: yup.array().nullable(),
							polygon: yup.string().polygon().required()
						})
						.required()
				: [
						yup
							.object()
							.shape({
								areaName: yup.string().nullable().required(),
								countryName: yup.string().nullable(),
								cityName: yup.string().nullable(),
								capacity: yup.number().nullable().required(),
								description: yup.string().nullable(),
								priority: yup.string().nullable().required(),
								drivers: yup.array().nullable()
							})
							.required(),
						yup
							.object()
							.shape({
								polygon: yup.string().polygon().required()
							})
							.required()
					],
		[]
	);

	const tabs = useMemo(
		() => [
			{
				label: 'areaInfo',
				component: () => <AreaDetails mode={mode} />,
				fields: ['areaName', 'countryName', 'cityName', 'capacity', 'description', 'priority', 'drivers'],
				error: errorTabs.includes('areaInfo')
			},
			{
				label: 'location',
				component: () => <AreaLocation mode={mode} setMap={setMap} />,
				fields: ['polygon'],
				error: errorTabs.includes('location')
			}
		],
		[mode, errorTabs]
	);

	const progressWizardTabs = () => {
		switch (tab) {
			case 0:
				return (
					<Form formMethods={formMethods}>
						<AreaDetails mode={mode} />
					</Form>
				);
			case 1:
				return (
					<Form formMethods={formMethods}>
						<AreaLocation mode={mode} setMap={setMap} />
					</Form>
				);

			default:
				return null;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(!addMode ? schema : schema[tab]),
		defaultValues: {
			areaName: null,
			countryName: null,
			cityName: null,
			capacity: null,
			polygon: null,
			description: null,
			priority: null,
			drivers: null
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const [map, setMap] = useState(null);
	const maps = window?.google?.maps;

	const VerticesToStringArray = (vertices) => {
		const verticesArray = [];
		vertices.forEach((vertex, index) => {
			const x = vertex.lat(index);
			const y = vertex.lng(index);
			verticesArray.push(`${x} ${y}`);
		});
		if (verticesArray.length && verticesArray[0] !== verticesArray[verticesArray.length - 1]) {
			verticesArray.push(verticesArray[0]);
		}
		return verticesArray.toString();
	};

	const initPolygon = async (coordinates) => {
		const bounds = new maps.LatLngBounds();
		const coords = [
			{ lat: 25.774, lng: -80.19 },
			{ lat: 18.466, lng: -66.118 },
			{ lat: 32.321, lng: -64.757 }
		];
		const { Polygon } = await maps.importLibrary('maps');

		const area = new Polygon({
			paths: coordinates ? coordinates : coords,
			clickable: !viewMode,
			draggable: !viewMode,
			editable: !viewMode,
			fillColor: '#ee8888',
			fillOpacity: 0.35,
			strokeColor: '#ee8888',
			strokeOpacity: 1.0,
			strokeWeight: 2,
			zIndex: 1
		});
		area.setMap(map);

		coordinates?.forEach((item) => {
			const { lat, lng } = item;
			bounds.extend({ lat: parseFloat(lat), lng: parseFloat(lng) });
		});
		map.fitBounds(bounds);

		const vertices = area.getPath();
		setValue('polygon', VerticesToStringArray(vertices));

		maps.event.addListener(vertices, 'set_at', () => {
			setValue('polygon', VerticesToStringArray(vertices), {
				shouldValidate: true,
				shouldDirty: true
			});
		});
		maps.event.addListener(vertices, 'insert_at', () => {
			setValue('polygon', VerticesToStringArray(vertices), {
				shouldValidate: true,
				shouldDirty: true
			});
		});
	};

	const initMap = async () => {
		const { DrawingManager } = await maps.importLibrary('drawing');

		const drawingManager = new DrawingManager({
			drawingMode: maps.drawing.OverlayType.POLYGON,
			drawingControl: true,
			drawingControlOptions: {
				position: maps.ControlPosition.TOP_CENTER,
				drawingModes: [maps.drawing.OverlayType.POLYGON]
			},
			polygonOptions: {
				draggable: true,
				editable: true,
				fillColor: '#ee8888',
				fillOpacity: 0.35,
				strokeColor: '#ee8888',
				strokeOpacity: 1.0,
				strokeWeight: 2,
				zIndex: 1
			}
		});
		drawingManager.setMap(map);
		maps.event.addListener(drawingManager, 'overlaycomplete', (polygon) => {
			drawingManager.setDrawingMode(null);
			drawingManager.drawingControl = false;
			drawingManager.setMap(null);

			const path = polygon.overlay.getPath();
			const polygonArray = path.getArray();
			const coordinates = polygonArray.map((coordinate) => ({
				lat: coordinate.lat(),
				lng: coordinate.lng()
			}));
			path.clear();
			initPolygon(coordinates);
		});
	};

	const coordinatesToStringArray = (coords) =>
		coords.reduce((acc, coord) => {
			const coordStr = `${coord.lat} ${coord.lng}`;
			if (acc) {
				return `${acc},${coordStr}`;
			} else {
				return coordStr;
			}
		}, '');

	const fillForm = () => {
		setIsLoading(true);
		axiosPrivate
			.post(ADVANCED_OMS.GET_AREA_SETTING, {
				ID: id
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						ID: data.ID ?? null,
						areaName: data.areaName ?? null,
						countryName: data.countryName ?? null,
						cityName: data.cityName ?? null,
						capacity: data.capacity ?? null,
						polygon: data.polygon
							? viewMode
								? coordinatesToStringArray(data.polygon)
								: data.polygon
							: null,
						description: data.description ?? null,
						drivers: data.drivers ?? null,
						priority: data.priority ?? null
					});
				} else AppLogger.error(response);
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => setIsLoading(false));
	};

	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	useEffect(() => {
		map &&
			maps.event.addListenerOnce(map, 'idle', () => {
				if (id) {
					const polygon = watch('polygon');
					if (Array.isArray(polygon)) {
						initPolygon(polygon);
					} else {
						const coordArr = polygon.split(',');
						const latLngArr = coordArr.map((coord) => {
							const splitted = coord.split(' ');
							return { lat: parseFloat(splitted[0]), lng: parseFloat(splitted[1]) };
						});
						initPolygon(latLngArr);
					}
				} else {
					initMap();
				}
			});
	}, [map]);

	const onSubmit = async (data) => {
		setIsLoading(true);
		// row means we are in edit mode else is add new branch.
		const URL = id ? ADVANCED_OMS.UPDATE_AREA_SETTING : ADVANCED_OMS.ADD_AREA_SETTING;
		try {
			await axiosPrivate.post(URL, {
				ID: id,
				areaName: data.areaName,
				cityName: data.cityName,
				countryName: data.countryName,
				capacity: data.capacity,
				polygon: data.polygon,
				description: data.description,
				drivers: data.drivers,
				priority: data.priority
			});
			initMap();
			showSuccess();
			dispatch(updateDynamicListData(DYNAMICLISTS.AREA_SETTINGS));
			handleCloseModal();
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	const onError = (errors) => {
		AppLogger.log(errors);
		const errorTabs = [];

		tabs.forEach((tab) => {
			if (tab.fields?.some((field) => errors[field])) {
				errorTabs.push(tab.label);
			}
		});
		if (errorTabs.length > 0) {
			showError({
				header: t('error.validationError'),
				message: t('error.tabsHaveErrors', {
					tabNames: errorTabs.map((tab) => t(tab)).join(', '),
					count: errorTabs.length
				})
			});
		}
		setErrorTabs(errorTabs);
	};

	return (
		<Modal fullwidth maxWidth="large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">formName.area</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{!addMode ? (
						<Form formMethods={formMethods}>
							<Tabs data={tabs} />
						</Form>
					) : (
						<>
							<ProgressWizard
								stages={[
									{ name: 'areaInfo', icon: faUserTag },
									{ name: 'location', icon: faBoxCheck }
								]}
								setIndex={setTab}
								index={tab}
								gutterBottom
							/>
							{progressWizardTabs()}
						</>
					)}
				</Form>
			</ModalContent>
			{!viewMode ? (
				<ModalActions>
					<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
					{tab !== 0 && addMode && (
						<Button
							iconPos="right"
							label="previous"
							variant="outline"
							icon={faAngleLeft}
							onClick={() => prevStep()}
						/>
					)}
					{tab < 1 && addMode ? (
						<Button
							icon={faAngleRight}
							label="continue"
							iconPos="right"
							onClick={handleSubmit(nextStep)}
							variant="blue"
							loading={isLoading}
						/>
					) : null}
					{(tab === 1 || !addMode) && (
						<>
							<Button
								label={id ? 'save' : 'add'}
								disabled={!isDirty || viewMode}
								disabledTooltip={!isDirty && 'formInfo.noChanges'}
								onClick={handleSubmit(onSubmit, onError)}
								iconPos="right"
							/>
						</>
					)}
				</ModalActions>
			) : (
				<></>
			)}
		</Modal>
	);
});

export default AreaSettingForm;
